import { AudioLines, ImageOff } from "lucide-react";
import type { AssetType } from "@/lib/api";
import { MediaPlayer } from "@/components/media/MediaPlayer";
import { cn } from "@/lib/utils";

interface Props {
  type: AssetType;
  url: string | null;
  poster?: string | null;
  alt?: string;
  variant?: "preview" | "theater";
  fit?: "contain" | "cover";
  className?: string;
  autoPlay?: boolean;
  playOnHover?: boolean;
  playWhenVisible?: boolean;
  lazyPreload?: boolean;
  durationSeconds?: number | null;
  testId?: string;
  onClick?: () => void;
  onImageError?: () => void;
  /** Video theater hooks, see MediaPlayer. */
  arrowKeysSeek?: boolean;
  onToggleFullscreen?: () => void;
  fullscreenActive?: boolean;
}

export function AssetMedia({
  type,
  url,
  poster,
  alt = "",
  variant = "preview",
  fit = "contain",
  className,
  autoPlay,
  playOnHover,
  playWhenVisible,
  lazyPreload,
  durationSeconds,
  testId,
  onClick,
  onImageError,
  arrowKeysSeek,
  onToggleFullscreen,
  fullscreenActive,
}: Props) {
  if (!url) {
    return (
      <span
        className={cn(
          "flex h-full w-full flex-col items-center justify-center gap-2 text-studio-muted",
          className
        )}
      >
        <ImageOff className="h-7 w-7 text-studio-muted/50" />
        <span className="text-[11px]">Preview unavailable</span>
      </span>
    );
  }

  if (type === "video") {
    return (
      <MediaPlayer
        src={url}
        poster={poster}
        variant={variant}
        fit={fit}
        label={alt}
        className={className}
        autoPlay={autoPlay}
        playOnHover={playOnHover}
        playWhenVisible={playWhenVisible}
        lazyPreload={lazyPreload}
        durationSeconds={durationSeconds}
        testId={testId}
        onClick={onClick}
        arrowKeysSeek={arrowKeysSeek}
        onToggleFullscreen={onToggleFullscreen}
        fullscreenActive={fullscreenActive}
      />
    );
  }

  if (type === "audio") {
    return (
      <div
        className={cn(
          "flex h-full w-full flex-col items-center justify-center gap-4 bg-gradient-to-br from-studio-card to-studio-bg px-3",
          className
        )}
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-studio-accent/15">
          <AudioLines className="h-7 w-7 text-studio-accent" />
        </span>
        <audio src={url} controls autoPlay={autoPlay} className="w-full max-w-[92%]" />
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={alt}
      onError={onImageError}
      onClick={onClick}
      loading={lazyPreload ? "lazy" : undefined}
      decoding="async"
      className={cn(
        fit === "cover" ? "h-full w-full object-cover" : "max-h-full max-w-full object-contain",
        className
      )}
    />
  );
}
